import { useEffect } from 'react'
import { NavigateFunction } from 'react-router-dom'

import { betflowcard1, betflowcard2, lightning } from '@/assets/images'
import Container from '@/components/atoms/container/container'
import Footer from '@/components/organisms/footer/footer'
import Header from '@/components/organisms/header/header'
import { VoucherPurchaseStep } from '@/data/enums/voucherPurchaseSteps'
import { useTranslate } from '@/i18n/useTranslate'
import { useOrder } from '@/providers'

type BettingFlowSelectionTemplateProps = {
    navigate: NavigateFunction
}

type BettingFlow = 'direct' | 'printed'

export default function BettingFlowSelectionTemplate({
    navigate
}: BettingFlowSelectionTemplateProps) {
    const { t } = useTranslate()
    const { setVoucherType, setCurrentStep } = useOrder()

    useEffect(() => {
        setVoucherType('betting')
    }, [setVoucherType])

    const handleFlowSelection = (flow: BettingFlow) => {
        setVoucherType('betting')
        setCurrentStep(VoucherPurchaseStep.SELECT_PAYMENT_METHOD)

        if (flow === 'direct') {
            navigate('/betting-service-selection', {
                state: {
                    voucherType: 'betting',
                    bettingFlow: flow
                }
            })
            return
        }

        navigate('/payment-method', {
            state: {
                voucherType: 'betting',
                bettingFlow: flow
            }
        })
    }

    return (
        <Container isFullHeight={true}>
            <Header
                navigateBackUrl="/"
                navigationBackText={t('header.back')}
            />
            <div className="betting-flow-selection">
                <h1>{t('bettingFlowSelection.title')}</h1>

                <div className="betting-flow-selection__cards">
                    <article
                        className="betting-flow-selection__card betting-flow-selection__card--primary"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleFlowSelection('direct')}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleFlowSelection('direct')
                            }
                        }}>
                        <img
                            className="betting-flow-selection__lightning"
                            src={lightning}
                            alt=""
                            aria-hidden="true"
                        />
                        <div className="betting-flow-selection__content">
                            <h2>{t('bettingFlowSelection.direct.title')}</h2>
                            <ul>
                                <li>{t('bettingFlowSelection.direct.bullets.0')}</li>
                                <li>{t('bettingFlowSelection.direct.bullets.1')}</li>
                                <li>{t('bettingFlowSelection.direct.bullets.2')}</li>
                                <li>{t('bettingFlowSelection.direct.bullets.3')}</li>
                            </ul>
                        </div>
                        <div className="betting-flow-selection__image-wrapper">
                            <img
                                src={betflowcard1}
                                alt={t('bettingFlowSelection.direct.imageAlt')}
                            />
                        </div>
                        <button
                            className="betting-flow-selection__action"
                            type="button">
                            {t('bettingFlowSelection.selectCta')}
                        </button>
                    </article>

                    <article
                        className="betting-flow-selection__card betting-flow-selection__card--secondary"
                        role="button"
                        tabIndex={0}
                        onClick={() => handleFlowSelection('printed')}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault()
                                handleFlowSelection('printed')
                            }
                        }}>
                        <div className="betting-flow-selection__content">
                            <h2>{t('bettingFlowSelection.printed.title')}</h2>
                            <ul>
                                <li>{t('bettingFlowSelection.printed.bullets.0')}</li>
                                <li>{t('bettingFlowSelection.printed.bullets.1')}</li>
                                <li>{t('bettingFlowSelection.printed.bullets.2')}</li>
                            </ul>
                        </div>
                        <div className="betting-flow-selection__image-wrapper">
                            <img
                                src={betflowcard2}
                                alt={t('bettingFlowSelection.printed.imageAlt')}
                            />
                        </div>
                        <button
                            className="betting-flow-selection__action"
                            type="button">
                            {t('bettingFlowSelection.selectCta')}
                        </button>
                    </article>
                </div>
            </div>
            <Footer />
        </Container>
    )
}
